import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useRootStore } from 'stores/RootStore'
import { usePendingBrandPlanSchedules } from 'hooks/queries/schedules/usePendingBrandPlanSchedules'
import { usePollBrandPlanSchedule } from 'hooks/queries/schedules/usePollBrandPlanSchedule'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import {
  contentHasPreview,
  getGeneratingSchedule,
  hasFailed,
  hasCompleted,
  isContentFromLatestBatch,
  isGeneratingContent,
  isGenerationDone,
  isPending,
  isSuccessful,
} from 'screens/ScheduledPosts/utils'
import { reportError } from 'utils/error'
import { useGenerationCompleteNotification } from './useGenerationCompleteNotification'

const MAX_REFETCHES = 3
const INITIAL_BACKOFF_MS = 1000
const BACKOFF_MULTIPLIER = 2
const PENDING_POLL_MS = 5000
const FINISHED_DISPLAY_DURATION_MS = 8000

/**
 * Global watcher that detects active content generation and updates the
 * ContentGenerationStore.
 */
export const useContentGenerationWatcher = () => {
  const { contentGeneration: store } = useRootStore()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const canRefetchPendingSchedules = useFlipperFlag('calendar_refetch_pending_schedules')
  const retryCountRef = useRef(0)
  const prevPendingCountRef = useRef(0)
  const completionFiredRef = useRef(false)
  const { notify } = useGenerationCompleteNotification()

  //polls for pending schedules
  const { data: schedules } = usePendingBrandPlanSchedules({
    refetchInterval: (data) => {
      const generatingSchedule = findGeneratingSchedule(data)
      if (generatingSchedule) {
        retryCountRef.current = 0
        return false
      }
      if (canRefetchPendingSchedules && retryCountRef.current < MAX_REFETCHES) {
        const backoffDelay = INITIAL_BACKOFF_MS * Math.pow(BACKOFF_MULTIPLIER, retryCountRef.current)
        retryCountRef.current += 1
        return backoffDelay
      }
      return PENDING_POLL_MS
    },
    refetchIntervalInBackground: false,
  })

  const generatingSchedule = useMemo(() => findGeneratingSchedule(schedules), [schedules])

  useEffect(() => {
    if (generatingSchedule) {
      retryCountRef.current = 0
      completionFiredRef.current = false
      store.setGeneratingSchedule(generatingSchedule.id, generatingSchedule.brand_plan_id)
    }
  }, [generatingSchedule, store])

  //polls the individual schedule for progress
  const activeScheduleId = generatingSchedule?.id ?? store.generatingScheduleId
  const { data: schedule } = usePollBrandPlanSchedule(activeScheduleId || 0, {
    enabled: !!activeScheduleId,
  })

  const numberOfNewPosts = store.numberOfNewPosts
  const regeneratingContentIds = store.regeneratingContentIds

  const newPostsRequestIds = useMemo(() => {
    if (!schedule) return undefined
    if (numberOfNewPosts) {
      const ids = schedule.contents.map((content) => content.id)
      return ids.sort((a, b) => a - b).slice(-numberOfNewPosts)
    }
    return undefined
  }, [numberOfNewPosts, schedule])

  //derives new content and all counts in a single pass
  const { newContent, generatingCount, completedCount, failedCount, successCount } = useMemo(() => {
    if (!schedule) {
      return { newContent: [], generatingCount: 0, completedCount: 0, failedCount: 0, successCount: 0 }
    }

    const useExplicitRegenScope = regeneratingContentIds.length > 0
    let filtered: typeof schedule.contents
    if (useExplicitRegenScope) {
      filtered = schedule.contents.filter((c) => regeneratingContentIds.includes(c.id))
    } else if (newPostsRequestIds) {
      filtered = schedule.contents.filter(
        (content) => newPostsRequestIds.includes(content.id) && isContentFromLatestBatch(content),
      )
    } else {
      filtered = schedule.contents.filter((content) => isContentFromLatestBatch(content))
    }

    let generating = 0
    let completed = 0
    let failed = 0
    let successful = 0
    for (const content of filtered) {
      if (useExplicitRegenScope) {
        if (!hasFailed(content) && !contentHasPreview(content)) generating++
      } else if (isGeneratingContent(content)) {
        generating++
      }
      if (hasCompleted(content)) completed++
      if (hasFailed(content)) failed++
      if (isSuccessful(content)) successful++
    }

    return {
      newContent: filtered,
      generatingCount: generating,
      completedCount: completed,
      failedCount: failed,
      successCount: successful,
    }
  }, [schedule, newPostsRequestIds, regeneratingContentIds])

  const latestBatchContents = schedule?.contents.filter(isContentFromLatestBatch) || []

  let isThisGenerationDone = false
  if (regeneratingContentIds.length > 0 && schedule) {
    isThisGenerationDone = regeneratingContentIds.every((id) => {
      const c = schedule.contents.find((x) => x.id === id)
      if (c == null) return false
      // Content completed (failed or has preview), or timed out of the active-generation window
      return hasFailed(c) || contentHasPreview(c) || !isGeneratingContent(c)
    })
  } else if (schedule) {
    isThisGenerationDone = isGenerationDone(schedule) && (!isPending(schedule.status) || latestBatchContents.length > 0)
  }

  //updates store with progress and invalidates calendar queries when generating count changes
  useEffect(() => {
    if (!activeScheduleId) return
    // Avoid clearing pre-seeded regeneration placeholders (ContentGenerationStore.initRegeneration)
    // while the schedule detail query has not returned yet.
    if (!schedule) return

    store.updateProgress(newContent, completedCount, failedCount, successCount)

    if (!isThisGenerationDone && generatingCount > 0 && prevPendingCountRef.current !== generatingCount) {
      queryClient.invalidateQueries(calendarKeys.all)
      prevPendingCountRef.current = generatingCount
    }
  }, [
    store,
    activeScheduleId,
    schedule,
    newContent,
    generatingCount,
    completedCount,
    failedCount,
    successCount,
    isThisGenerationDone,
    queryClient,
  ])

  //handles generation completion
  useEffect(() => {
    if (!isThisGenerationDone || !activeScheduleId) return
    if (completionFiredRef.current) return

    // When regenerating across multiple schedules, the current schedule may
    // finish while placeholders from other schedules are still pending.
    // Instead of marking everything done, clean up the finished schedule and
    // let the watcher transition to the next generating schedule naturally.
    if (store.hasOtherScheduleContents) {
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
      queryClient.removeQueries(scheduleKeys.detail(activeScheduleId))
      return
    }

    completionFiredRef.current = true
    store.markDone()
    notify()

    queryClient.invalidateQueries(calendarKeys.all)
    queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))

    const timeout = setTimeout(() => {
      if (store.isActive) return
      queryClient.removeQueries(scheduleKeys.detail(activeScheduleId))
      queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
      store.reset()
      retryCountRef.current = 0
      prevPendingCountRef.current = 0
      completionFiredRef.current = false
      // Re-invalidate the calendar after temp events are cleared so the final
      // fetch picks up images/content that may not have been ready during the
      // first invalidation at completion time.
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
    }, FINISHED_DISPLAY_DURATION_MS)

    return () => clearTimeout(timeout)
  }, [isThisGenerationDone, activeScheduleId, store, queryClient, workspaceId, notify])
}

//finds the generating schedule
function findGeneratingSchedule(schedules?: Parameters<typeof getGeneratingSchedule>[0]) {
  if (!schedules) return undefined

  try {
    return getGeneratingSchedule(schedules)
  } catch (error) {
    reportError(error, {
      severity: 'error',
      metaData: { hook: 'useContentGenerationWatcher' },
    })
    return undefined
  }
}
